import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { fr, enUS } from "date-fns/locale";
import FilterChip from "src/common/components/FilterChip/FilterChip";
import ReleaseColumn from "src/features/releases/components/ReleaseColumn";

/**
 * `/releases` — the full "What's new" timeline (Phase 8).
 *
 * A thin shell around {@link ReleaseColumn}: the surface filter chips drive the
 * `?surface=` query param and pick the layout. "Spatial Studio" (vr) and
 * "Editor Web" (web) each render a single full-width column; "Tous" renders both
 * side by side. Each column paginates, groups by month and scrolls on its own —
 * only the columns scroll, the page around them (title + chips) stays put.
 * Desktop-only — registered in the desktop Router tree.
 */

type SurfaceFilter = "all" | "web" | "vr";

const Releases = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  const [searchParams, setSearchParams] = useSearchParams();
  const surfaceParam = searchParams.get("surface");
  const surface: SurfaceFilter =
    surfaceParam === "web" || surfaceParam === "vr" ? surfaceParam : "all";

  const setSurface = (next: SurfaceFilter) => {
    if (next === "all") setSearchParams({});
    else setSearchParams({ surface: next });
  };

  const filters: SurfaceFilter[] = ["all", "vr", "web"];

  return (
    <div className="flex h-full flex-col">
      <header className="z-50 shrink-0 bg-white shadow">
        <div className="mx-auto ml-4 px-4 py-6">
          <h1 className="text-3xl font-bold text-black">{t("pages.releases.title")}</h1>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col px-4 pt-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex shrink-0 gap-2">
          {filters.map((f) => (
            <FilterChip
              key={f}
              label={t(`pages.releases.filter.${f}`)}
              active={surface === f}
              onClick={() => setSurface(f)}
            />
          ))}
        </div>

        <div className="flex min-h-0 flex-1 gap-8">
          {surface === "all" ? (
            <>
              <ReleaseColumn
                key="vr"
                surface="vr"
                locale={locale}
                title={t("pages.releases.filter.vr")}
              />
              <ReleaseColumn
                key="web"
                surface="web"
                locale={locale}
                title={t("pages.releases.filter.web")}
              />
            </>
          ) : (
            <ReleaseColumn key={surface} surface={surface} locale={locale} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Releases;
